import { useCallback, useState } from "react";
import { useAuth } from "@/features/auth/contexts/AuthContext";

interface ReportPrefs {
	pinned: string[];
	/** How often each report was opened, for "Most used". */
	opens: Record<string, number>;
}

const EMPTY: ReportPrefs = { pinned: [], opens: {} };

const storageKey = (userId: unknown) => `reports.prefs.${userId ?? "anon"}`;

/** A per-browser convenience only: unreadable or blocked storage just means no pins or usage history. */
function readPrefs(key: string): ReportPrefs {
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return EMPTY;
		const parsed = JSON.parse(raw);
		return {
			pinned: Array.isArray(parsed?.pinned) ? parsed.pinned.filter((x: unknown) => typeof x === "string") : [],
			opens: parsed?.opens && typeof parsed.opens === "object" ? parsed.opens : {},
		};
	} catch {
		return EMPTY;
	}
}

function writePrefs(key: string, prefs: ReportPrefs) {
	try {
		window.localStorage.setItem(key, JSON.stringify(prefs));
	} catch {
		// Storage full or blocked (private mode): pins simply do not persist.
	}
}

/** Pinned reports and open counts, per signed-in user, kept in this browser. */
export function useReportPrefs() {
	const { user } = useAuth();
	const key = storageKey(user?.id);
	const [prefs, setPrefs] = useState<ReportPrefs>(() => readPrefs(key));

	const update = useCallback(
		(change: (prev: ReportPrefs) => ReportPrefs) => {
			// Re-read so a report opened in another tab is not overwritten.
			const next = change(readPrefs(key));
			writePrefs(key, next);
			setPrefs(next);
		},
		[key],
	);

	const togglePin = useCallback(
		(id: string) =>
			update(p => ({
				...p,
				pinned: p.pinned.includes(id) ? p.pinned.filter(x => x !== id) : [...p.pinned, id],
			})),
		[update],
	);

	const recordOpen = useCallback(
		(id: string) => update(p => ({ ...p, opens: { ...p.opens, [id]: (Number(p.opens[id]) || 0) + 1 } })),
		[update],
	);

	return { pinned: prefs.pinned, opens: prefs.opens, togglePin, recordOpen };
}
